import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { Controller } from './types';

type ActionCreator = (...args: unknown[]) => unknown;

export type DispatchActions<T extends Record<string, ActionCreator>> = {
  [K in keyof T]: (...args: Parameters<T[K]>) => Promise<unknown>;
};

const useDispatchActions = <T extends Controller>(controller: T): DispatchActions<T['actions']> => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  return useMemo(() => {
    // Reduce the controller's actions into a set of dispatch-ready functions.
    // Each wrapper builds the `{ args, extra }` envelope so the thunk can
    // forward `navigate` into its action body's thunkApi (and onSuccess
    // handlers can call `navigate(url)` to set pageState.navigateTo).
    return Object.keys(controller.actions).reduce(
      (acc, key) => {
        acc[key] = (...args: unknown[]) => {
          const dispatchArgs = {
            args,
            extra: {
              navigate,
            },
          };
          return (dispatch as (a: unknown) => Promise<unknown>)(
            (controller.actions[key] as (envelope: unknown) => unknown)(dispatchArgs),
          );
        };

        return acc;
      },
      {} as Record<string, (...args: unknown[]) => Promise<unknown>>,
    );
  }, [controller.actions, dispatch, navigate]) as DispatchActions<T['actions']>;
};

export default useDispatchActions;
